import type { RefinementCtx } from 'zod'
import { ApplicationData, EvaluateOptions, Issue, JobData } from './types'
import { evaluateRules } from './rules'
import { evaluateJobRules } from './job'

/**
 * Adapters from the rule set to the places that consume it. The shapes stay the same everywhere so a gate, a form
 * and a test all see the same issue for the same data.
 */

const addZodIssues = (ctx: RefinementCtx, issues: Issue[]): void => {
  for (let i = 0; i < issues.length; i++) {
    const issue = issues[i]
    if (issue.severity !== 'error') continue
    ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message, params: { ruleId: issue.ruleId } })
  }
}

/**
 * A zod `superRefine` for an application form: `schema.superRefine(zodRefinement({ at: 'addresses', countryLabelFor }))`.
 * Only errors become zod issues; warnings never block.
 */
export const zodRefinement = (options: EvaluateOptions) => (value: ApplicationData, ctx: RefinementCtx): void =>
  addZodIssues(ctx, evaluateRules(value, options))

/** A zod `superRefine` for a job-role form */
export const jobZodRefinement = () => (value: JobData, ctx: RefinementCtx): void =>
  addZodIssues(ctx, evaluateJobRules(value))

/** Issues as a flat list for an API body or a log: `{ ruleId, field: 'CurrentAddress.Address.Postcode', message, severity }` */
export const flattenIssues = (issues: Issue[]): { ruleId: string, field: string, message: string, severity: Issue['severity'] }[] =>
  issues.map(issue => ({ ruleId: issue.ruleId, field: issue.path.join('.'), message: issue.message, severity: issue.severity }))
